import { NEFI_FIRST_SECTION_INPUTS } from 'components/NEFIComponents/NEFISteps/NEFIStepForm/NEFIFormUtility';

import {
  BRANCH_TYPE,
  COUNTRY_OPTIONS,
  EINTRAGUNG_ALS_ID,
  EIV_ENTRY_TYPE,
  EIV_ENTRY_TYPE_TO_GERMAN,
  INSTALLER_ENTRIES,
  KEYS_TO_GERMAN,
  PFLEGENDE_BU,
  TENANT_ID,
  TENANT_WITH_BIRTHDATE,
} from 'global/constants';
import { evaluateCondition } from 'global/fieldValidationFunction';

import {
  branchToCapital,
  conditionalStaticEntry,
  getEintragungAlsIdEntries,
  prepValueForEmail,
} from 'utils/DataUtils';
import { prepDocumentForMutation } from 'utils/FileUtil';
import { areExpertContactsEnabled } from 'utils/FormUtils';
import {
  isInstallerRegisteringForBranch,
  isRegisteringBranchGuest,
  isRegisteringBranchMain,
  isRegisteringInstallerOnlyHilfsbetrieb,
} from 'utils/InstallerUtils';
import { QUALIFICATION_ID_FIELDS } from 'utils/qualificationBasisData';
import {
  PFLEGENDE_BU_TO_GRIDNET_PROVIDER,
  getPflegendeBUForBranchSelection,
  getTenantId,
} from 'utils/tenantUtil';

const NEFI_FIELD_LABEL_OVERRIDES = {
  companyOwner: 'Ansprechpartner',
};

const getNEFIFieldLabel = (fieldName) =>
  NEFI_FIELD_LABEL_OVERRIDES[fieldName] ?? KEYS_TO_GERMAN[fieldName];

const getNEFIBranchEntryTypeLabel = (branch, branchSelectionValues) => {
  const branchType = branchSelectionValues[`branchType${branchToCapital(branch)}`];
  if (branchType) {
    return EIV_ENTRY_TYPE_TO_GERMAN[branchType];
  }

  return EIV_ENTRY_TYPE_TO_GERMAN[EIV_ENTRY_TYPE.GUEST];
};

const getNEFICompanyType = (firstPageValues, comboBoxOptions) => {
  const selectedValue = firstPageValues.taetigkeitsschwerpunkt || '';
  const selectedOption = comboBoxOptions?.taetigkeitsschwerpunkt?.find(
    (option) => option.title === selectedValue || String(option.value) === String(selectedValue)
  );

  return selectedOption?.value || selectedValue;
};

const prepNEFIExperts = (firstPageValues, branchSelectionValues) => {
  const experts = [...firstPageValues.experts];
  const expertEntries = [];
  const isEnergyGuest = isRegisteringBranchGuest(BRANCH_TYPE.ENERGY, branchSelectionValues);
  const isGasGuest = isRegisteringBranchGuest(BRANCH_TYPE.GAS, branchSelectionValues);

  experts.forEach((expert) => {
    expertEntries.push(
      [
        conditionalStaticEntry(
          expert.isExpertEnergyActive,
          'Sparte Strom',
          prepValueForEmail(expert.isExpertEnergyActive)
        ),
        conditionalStaticEntry(
          expert.isExpertGasActive,
          'Sparte Gas',
          prepValueForEmail(expert.isExpertGasActive)
        ),
        { first: 'Vorname', second: prepValueForEmail(expert.firstName) },
        { first: 'Nachname', second: prepValueForEmail(expert.lastName) },
        ...QUALIFICATION_ID_FIELDS.map(([field]) => ({
          first: field,
          second: String(expert[field] || 0),
        })),
        conditionalStaticEntry(
          areExpertContactsEnabled && expert.telefon,
          'Telefonnummer',
          prepValueForEmail(expert.telefon)
        ),
        conditionalStaticEntry(
          areExpertContactsEnabled && expert.netzportalEmail,
          'E-Mail-Adresse',
          prepValueForEmail(expert.netzportalEmail)
        ),
        conditionalStaticEntry(
          expert.isExpertEnergyActive && isEnergyGuest,
          'Stromausweis Gültigkeitsdatum',
          prepValueForEmail(expert.energyValidityDate)
        ),
        conditionalStaticEntry(
          expert.isExpertGasActive && isGasGuest,
          'Gasausweis Gültigkeitsdatum',
          prepValueForEmail(expert.gasValidityDate)
        ),
        conditionalStaticEntry(
          TENANT_WITH_BIRTHDATE.includes(getTenantId()),
          'Geburtsdatum',
          prepValueForEmail(expert.birthdate)
        ),
      ].filter(Boolean)
    );
  });
  return expertEntries;
};

export const prepNEFIDataForBackend = (
  firstPageValues,
  secondPageValues,
  subject,
  documents,
  branchSelectionValues,
  comboBoxOptions
) => {
  const formDataEntries = [];
  const expertEntries = prepNEFIExperts(firstPageValues, branchSelectionValues);

  NEFI_FIRST_SECTION_INPUTS.forEach((section) => {
    if (
      section.id !== 'avaExtraInfo' &&
      evaluateCondition(section.whenShouldSectionRender, {
        isOnlyHilfsbetrieb: isRegisteringInstallerOnlyHilfsbetrieb(branchSelectionValues),
        branchSelectionValues,
      })
    ) {
      if (section.fields) {
        section.fields.forEach((field) => {
          if (
            field.name !== 'email' &&
            field.name !== 'taetigkeitsschwerpunkt' &&
            evaluateCondition(field.whenShouldFieldRender, {
              isOnlyHilfsbetrieb: isRegisteringInstallerOnlyHilfsbetrieb(branchSelectionValues),
              branchSelectionValues,
            })
          ) {
            formDataEntries.push({
              first: getNEFIFieldLabel(field.name),
              second: prepValueForEmail(
                field.name === 'companyCountry'
                  ? (COUNTRY_OPTIONS.find(
                      (opt) =>
                        opt.value === firstPageValues[field.name] ||
                        opt.title === firstPageValues[field.name]
                    )?.title ?? firstPageValues[field.name])
                  : firstPageValues[field.name],
                field.name,
                comboBoxOptions
              ),
            });
          }
        });
      }
    }
  });

  const upsertFormDataEntry = (key, value) => {
    const fieldLabel = KEYS_TO_GERMAN[key];
    if (!fieldLabel) return;

    const nextEntry = {
      first: fieldLabel,
      second: prepValueForEmail(value, key, comboBoxOptions),
    };

    const existingIndex = formDataEntries.findIndex((entry) => entry.first === fieldLabel);
    if (existingIndex >= 0) {
      formDataEntries[existingIndex] = nextEntry;
    } else {
      formDataEntries.push(nextEntry);
    }
  };

  if (isInstallerRegisteringForBranch(BRANCH_TYPE.ENERGY, branchSelectionValues)) {
    upsertFormDataEntry('eintragungsartStrom', branchSelectionValues.branchTypeEnergy);
  }

  if (isInstallerRegisteringForBranch(BRANCH_TYPE.GAS, branchSelectionValues)) {
    upsertFormDataEntry('eintragungsartGas', branchSelectionValues.branchTypeGas);
  }

  const documentEntries = documents.map(prepDocumentForMutation);

  const getTenantIdSafe = () => {
    return getTenantId();
  };

  const getBUForBranch = (branch) => {
    const tenantId = getTenantIdSafe();
    const tenantDefaults = PFLEGENDE_BU[tenantId];

    if (!tenantDefaults) return '';

    if (branch === BRANCH_TYPE.ENERGY) {
      return tenantDefaults?.MTNSTROM ?? tenantDefaults?.[tenantId] ?? '';
    }

    if (branch === BRANCH_TYPE.GAS) {
      return tenantDefaults?.MTNGAS ?? tenantDefaults?.[tenantId] ?? '';
    }

    return '';
  };

  const resolveBUValue = (branch) => {
    const isMain = isRegisteringBranchMain(branch, branchSelectionValues);

    if (isMain) {
      return getBUForBranch(branch);
    }

    return branch === BRANCH_TYPE.ENERGY
      ? firstPageValues.gridProviderEnergy
      : firstPageValues.gridProviderGas;
  };

  const mapBUToGridNetProviderId = (bu) => {
    const tenantId = getTenantIdSafe();
    const mapping = PFLEGENDE_BU_TO_GRIDNET_PROVIDER?.[tenantId];

    if (!mapping || !bu) return '';

    return mapping?.[bu] ?? '';
  };

  const energyGridNetId = mapBUToGridNetProviderId(resolveBUValue(BRANCH_TYPE.ENERGY));

  const gasGridNetId = mapBUToGridNetProviderId(resolveBUValue(BRANCH_TYPE.GAS));

  const energyText = prepValueForEmail(energyGridNetId, 'gridProviderEnergy', comboBoxOptions);

  const gasText = prepValueForEmail(gasGridNetId, 'gridProviderGas', comboBoxOptions);

  if (isRegisteringBranchMain(BRANCH_TYPE.ENERGY, branchSelectionValues)) {
    formDataEntries.push({
      first: KEYS_TO_GERMAN.gridProviderEnergy,
      second: energyText,
    });
  }

  if (isRegisteringBranchMain(BRANCH_TYPE.GAS, branchSelectionValues)) {
    formDataEntries.push({
      first: KEYS_TO_GERMAN.gridProviderGas,
      second: gasText,
    });
  }

  const hasEnergyRow = isInstallerRegisteringForBranch(BRANCH_TYPE.ENERGY, branchSelectionValues)
    ? 'Strom'
    : '';
  const hasGasRow = isInstallerRegisteringForBranch(BRANCH_TYPE.GAS, branchSelectionValues)
    ? 'Gas'
    : '';
  const activeBranches = [hasEnergyRow, hasGasRow].filter((branch) => branch).join(' ');

  const baseDataEntries = [
    { first: 'Email', second: prepValueForEmail(firstPageValues.email) },
    { first: 'Sparte', second: prepValueForEmail(activeBranches) },
    conditionalStaticEntry(
      hasEnergyRow,
      'Status Strom Eintragung',
      getNEFIBranchEntryTypeLabel(BRANCH_TYPE.ENERGY, branchSelectionValues)
    ),
    conditionalStaticEntry(
      hasEnergyRow,
      'Betriebsart Strom',
      prepValueForEmail(getNEFICompanyType(firstPageValues, comboBoxOptions))
    ),
    conditionalStaticEntry(
      hasGasRow,
      'Status Gas Eintragung',
      getNEFIBranchEntryTypeLabel(BRANCH_TYPE.GAS, branchSelectionValues)
    ),
    conditionalStaticEntry(
      hasGasRow,
      'Betriebsart Gas',
      prepValueForEmail(getNEFICompanyType(firstPageValues, comboBoxOptions))
    ),
    ...getEintragungAlsIdEntries({
      energyId: EINTRAGUNG_ALS_ID.FACHINTERESSENT,
      gasId: EINTRAGUNG_ALS_ID.FACHINTERESSENT,
      hasEnergyBranch: Boolean(hasEnergyRow),
      hasGasBranch: Boolean(hasGasRow),
    }),
    { first: 'Anmerkung des Installateurs', second: prepValueForEmail(secondPageValues.changes) },
  ].filter(Boolean);

  const agreementEntries = [
    {
      first: 'Adressveröffentlichung',
      second: prepValueForEmail(secondPageValues.isAddressPublic),
    },
    { first: 'Infoversand E-Mail', second: prepValueForEmail(secondPageValues.canSendMail) },
  ];

  if (getTenantId() === TENANT_ID.AVA) {
    baseDataEntries.push({
      first: 'PLZ Auftragsort',
      second: firstPageValues.doesNeedAvaGuestPostal
        ? firstPageValues.avaGuestPostal
        : firstPageValues.companyPostal,
    });
  }

  return {
    email: firstPageValues.email,
    subject,
    baseDataEntries,
    formDataEntries,
    expertEntries,
    documents: documentEntries,
    agreementEntries,
    pflegendeBU: getPflegendeBUForBranchSelection(branchSelectionValues),
  };
};

export const createEmailSubjectForNEFI = (installerEntry, branchSelectionValues) => {
  let subject = '[e-fix] ';
  switch (installerEntry) {
    case INSTALLER_ENTRIES.MAIN_BOTH:
      subject += 'Strom Gas - Neu - Stamm';
      break;
    case INSTALLER_ENTRIES.MAIN_ENERGY_GUEST_GAS:
      subject += 'Strom Gas - Neu - Stamm Gast';
      break;
    case INSTALLER_ENTRIES.GUEST_ENERGY_MAIN_GAS:
      subject += 'Strom Gas - Neu - Gast Stamm';
      break;
    case INSTALLER_ENTRIES.MAIN_ENERGY_ONLY:
      subject += 'Strom - Neu - Stamm';
      break;
    case INSTALLER_ENTRIES.MAIN_GAS_ONLY:
      subject += 'Gas - Neu - Stamm';
      break;
    case INSTALLER_ENTRIES.GUEST_ONLY: {
      const hasEnergyRow = isInstallerRegisteringForBranch(
        BRANCH_TYPE.ENERGY,
        branchSelectionValues
      );
      const hasGasRow = isInstallerRegisteringForBranch(BRANCH_TYPE.GAS, branchSelectionValues);
      if (hasEnergyRow && hasGasRow) {
        subject += 'Strom Gas - Neu - Gast';
      } else if (hasEnergyRow) {
        subject += 'Strom - Neu - Gast';
      } else if (hasGasRow) {
        subject += 'Gas - Neu - Gast';
      }
      break;
    }
    default:
      subject = '';
      break;
  }

  return subject;
};

export const getInstallerEntryByBranchSelection = (branchSelectionValues) => {
  if (
    isRegisteringBranchMain(BRANCH_TYPE.ENERGY, branchSelectionValues) &&
    isRegisteringBranchMain(BRANCH_TYPE.GAS, branchSelectionValues)
  ) {
    return INSTALLER_ENTRIES.MAIN_BOTH;
  }
  if (
    isRegisteringBranchMain(BRANCH_TYPE.ENERGY, branchSelectionValues) &&
    isRegisteringBranchGuest(BRANCH_TYPE.GAS, branchSelectionValues)
  ) {
    return INSTALLER_ENTRIES.MAIN_ENERGY_GUEST_GAS;
  }
  if (
    isRegisteringBranchGuest(BRANCH_TYPE.ENERGY, branchSelectionValues) &&
    isRegisteringBranchMain(BRANCH_TYPE.GAS, branchSelectionValues)
  ) {
    return INSTALLER_ENTRIES.GUEST_ENERGY_MAIN_GAS;
  }
  if (
    isRegisteringBranchMain(BRANCH_TYPE.ENERGY, branchSelectionValues) &&
    !isInstallerRegisteringForBranch(BRANCH_TYPE.GAS, branchSelectionValues)
  ) {
    return INSTALLER_ENTRIES.MAIN_ENERGY_ONLY;
  }
  if (
    !isInstallerRegisteringForBranch(BRANCH_TYPE.ENERGY, branchSelectionValues) &&
    isRegisteringBranchMain(BRANCH_TYPE.GAS, branchSelectionValues)
  ) {
    return INSTALLER_ENTRIES.MAIN_GAS_ONLY;
  }
  if (
    isRegisteringBranchGuest(BRANCH_TYPE.ENERGY, branchSelectionValues) ||
    isRegisteringBranchGuest(BRANCH_TYPE.GAS, branchSelectionValues)
  ) {
    return INSTALLER_ENTRIES.GUEST_ONLY;
  }

  // NEFI branch selection only stores active branches; Fachinteressent entries are guest entries.
  if (
    isInstallerRegisteringForBranch(BRANCH_TYPE.ENERGY, branchSelectionValues) ||
    isInstallerRegisteringForBranch(BRANCH_TYPE.GAS, branchSelectionValues)
  ) {
    return INSTALLER_ENTRIES.GUEST_ONLY;
  }

  return '';
};

export const getInstallerEntryByExpert = (expert, branchSelectionValues) => {
  if (expert.isExpertEnergyActive && expert.isExpertGasActive) {
    if (
      isRegisteringBranchMain(BRANCH_TYPE.ENERGY, branchSelectionValues) &&
      isRegisteringBranchMain(BRANCH_TYPE.GAS, branchSelectionValues)
    ) {
      return INSTALLER_ENTRIES.MAIN_BOTH;
    }
    if (
      isRegisteringBranchGuest(BRANCH_TYPE.ENERGY, branchSelectionValues) &&
      isRegisteringBranchMain(BRANCH_TYPE.GAS, branchSelectionValues)
    ) {
      return INSTALLER_ENTRIES.GUEST_ENERGY_MAIN_GAS;
    }
    if (
      isRegisteringBranchMain(BRANCH_TYPE.ENERGY, branchSelectionValues) &&
      isRegisteringBranchGuest(BRANCH_TYPE.GAS, branchSelectionValues)
    ) {
      return INSTALLER_ENTRIES.MAIN_ENERGY_GUEST_GAS;
    }
    if (
      isRegisteringBranchGuest(BRANCH_TYPE.ENERGY, branchSelectionValues) &&
      isRegisteringBranchGuest(BRANCH_TYPE.GAS, branchSelectionValues)
    ) {
      return INSTALLER_ENTRIES.GUEST_ONLY;
    }
    return '';
  }
  if (expert.isExpertEnergyActive) {
    if (isRegisteringBranchMain(BRANCH_TYPE.ENERGY, branchSelectionValues)) {
      return INSTALLER_ENTRIES.MAIN_ENERGY_ONLY;
    }
    return INSTALLER_ENTRIES.GUEST_ONLY;
  }
  if (expert.isExpertGasActive) {
    if (isRegisteringBranchMain(BRANCH_TYPE.GAS, branchSelectionValues)) {
      return INSTALLER_ENTRIES.MAIN_GAS_ONLY;
    }
    return INSTALLER_ENTRIES.GUEST_ONLY;
  }
  return '';
};

export const isBranchCompanyTypeHilfsbetrieb = (branch, branchSelectionValues) =>
  branchSelectionValues[`companyType${branchToCapital(branch)}`] === 'Hilfsbetrieb';
